import { useCallback, useEffect, useRef, useState } from "react";
import { loadGooglePlaces, mapGoogleAddress, withPlacesTimeout } from "@/lib/googlePlaces";

export const useAddressSuggestions = ({ enabled, onSelect }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [status, setStatus] = useState("idle");
  const [activeIndex, setActiveIndex] = useState(-1);
  const revision = useRef(0);
  const timer = useRef();
  const session = useRef(null);
  const selectedValue = useRef("");

  const clear = useCallback(() => {
    revision.current += 1;
    window.clearTimeout(timer.current);
    setSuggestions([]);
    setActiveIndex(-1);
    setStatus("idle");
  }, []);

  useEffect(() => { clear(); }, [enabled, clear]);
  useEffect(() => () => {
    revision.current += 1;
    window.clearTimeout(timer.current);
  }, []);

  const search = input => {
    clear();
    if (!enabled || input.trim().length < 3 || input === selectedValue.current) return;
    const current = revision.current;
    timer.current = window.setTimeout(async () => {
      setStatus("loading");
      try {
        const places = await loadGooglePlaces();
        if (current !== revision.current) return;
        session.current ||= new places.AutocompleteSessionToken();
        const result = await withPlacesTimeout(places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input, sessionToken: session.current, includedRegionCodes: ["us"], language: "en-US",
        }));
        if (current !== revision.current) return;
        const options = result.suggestions.filter(item => item.placePrediction).slice(0, 5);
        setSuggestions(options);
        setActiveIndex(-1);
        setStatus(options.length ? "ready" : "empty");
      } catch {
        if (current === revision.current) setStatus("error");
      }
    }, 300);
  };

  const select = async index => {
    const prediction = suggestions[index]?.placePrediction;
    if (!prediction || !enabled) return;
    clear();
    const current = revision.current;
    session.current = null;
    setStatus("selecting");
    try {
      const place = prediction.toPlace();
      await withPlacesTimeout(place.fetchFields({ fields: ["addressComponents"] }));
      if (current !== revision.current) return;
      const address = mapGoogleAddress(place.addressComponents);
      if (address.country !== "US" || !address.address1) throw new Error("Incomplete address");
      selectedValue.current = address.address1;
      onSelect(address);
      setStatus("selected");
    } catch {
      if (current === revision.current) setStatus("error");
    }
  };

  return { suggestions, status, activeIndex, setActiveIndex, search, select, clear };
};